/**
 * mcrItemHelper.js
 *
 * Helper untuk memproses satu MCR item (object, bukan array).
 * Digunakan oleh validateSingleMcrItem dan preparePayloadSingleItem di McrFooter.vue.
 * Pola array (stateAllMcrItemCat) tetap ada di McrFooter.vue dan tidak diganti.
 */

// ─── Shared utilities ────────────────────────────────────────────────────────
const catField = (key, message) => ({ key, message });
export const isCatFieldEmpty = (v) => v === null || v === undefined || v === '' || (typeof v === 'string' && v.trim() === '');
const INALUM_EXEMPT_VALUATION_CLASS = ['ZHSE', 'ZLAB', 'ZNOV', 'ZNVL', 'ZPCK', 'HERS'];
const INALUM_EXEMPT_PRICE_CONTROL   = ['ZNOV', 'ZNVL', 'HERS'];

// ─── buildAntamDraftRulesForItem (saveDraftPeritemFormUserAntam) ────────────
/**
 * DIPANGGIL DI saveDraftPeritemFormUserAntam() DI McrFooter.vue.
 * Sengaja TIDAK memakai buildCatalogRulesForItem('ANTAM') / buildDynamicRulesForItem
 * karena field set-nya jauh lebih kecil — cuma field yang benar-benar dikirim oleh
 * buildAntamFormData() (McrFooter.vue), bukan payload cataloguer penuh. Dipakai
 * bareng validateMcrItemCore() supaya format hasil validasi (tab-grouped, dedup)
 * konsisten dengan validateSingleMcrItem()/saveCatalog().
 * @param {string} mcrType - this.mcrHeader.type dari McrFooter
 * @returns {Object} rules
 */
export function buildAntamDraftRulesForItem(mcrType) {
	const isMcr002 = (mcrType || '').toUpperCase() === 'MCR002';

	const basic = [
		catField('cat_material_type', 'Material Type'),
		catField('cat_material_group', 'Material Group'),
		catField('cat_uom', 'UoM'),
		catField('cat_industry_sector', 'Industry Sector'),
		catField('specification', 'Specification'),
	];
	if (isMcr002) basic.push(catField('material_number', 'Material Number'));

	// MCR002: mrp_group, mrp_type, lot_size, reorder_point, avail_check tidak wajib
	const mcr002ExemptMrpKeys = ['mrp_group', 'mrp_type', 'lot_size', 'reorder_point', 'avail_check'];
	const mrpFields = [
		catField('plant', 'Plant'),
		catField('mrp_group', 'MRP Group'),
		catField('mrp_type', 'MRP Type'),
		catField('lot_size', 'Lot Size'),
		catField('mrp_controller', 'MRP Controller'),
		catField('reorder_point', 'Reorder Point'),
		catField('planned_delivery', 'Planned Delivery'),
		catField('safety_stock', 'Safety Stock'),
		catField('avail_check', 'Availability Check'),
	].filter((f) => !(isMcr002 && mcr002ExemptMrpKeys.includes(f.key)));

	return {
		basic,
		sections: {
			// mandatory: false — pola lama cuma validasi baris yang sudah ada, tidak
			// mewajibkan minimal 1 baris di section MRP / Valuation.
			cat_emcr_item_plant: { tabKey: 'mrp', tabLabel: 'MRP', mandatory: false, fields: mrpFields },
			cat_emcr_item_valuation: {
				tabKey: 'accounting', tabLabel: 'Accounting', mandatory: false,
				fields: [
					catField('valuation_class', 'Valuation Class'),
					catField('price_control', 'Price Control'),
				],
			},
		},
		conditional: 'antamDraft',
	};
}

// ─── buildCatalogRules ───────────────────────────────────────────────────────
export function buildCatalogRulesForItem(companyCode) {
	const code = (companyCode || '').toUpperCase();
	if (!['ANTAM', 'TIMAH', 'INALUM'].includes(code)) return null;

	const rules = {
		basic: [
			catField('cat_inc', 'Item Name'),
			catField('cat_item_type', 'Item Type'),
			catField('cat_material_type', 'Material Type'),
			catField('cat_material_group', 'Material Group'),
			catField('cat_uom', 'UoM'),
		],
		sections: {
			cat_emcr_item_characteristic_value:    { tabKey: 'basic_data', tabLabel: 'Basic Data & Characteristic', mandatory: true, special: 'characteristic', fields: [] },
			cat_emcr_item_plants:                  { tabKey: 'general_plant', tabLabel: 'Plant Data', mandatory: true, fields: [catField('plant', 'Plant')] },
			cat_emcr_item_location:                { tabKey: 'location', tabLabel: 'Plant & Storage Location', mandatory: false, fields: [catField('plant', 'Plant'), catField('location', 'Location')] },
			cat_emcr_item_sale_general_plants:     { tabKey: 'qm_sales', tabLabel: 'Sales, Gen. Plant & Sales', mandatory: true, fields: [catField('plant', 'Plant')] },
			cat_emcr_item_plant:                   { tabKey: 'mrp', tabLabel: 'MRP', mandatory: true, fields: [catField('plant', 'Plant'), catField('mrp_type', 'MRP Type'), catField('avail_check', 'Availability Check')] },
			cat_emcr_item_manufacturer:            { tabKey: 'manufacturer', tabLabel: 'Manufacture & Equipment', mandatory: false, fields: [catField('manufacture', 'Manufacture'), catField('source_type', 'Source Type'), catField('manufacture_ref', 'Manufacture Ref'), catField('valid_until', 'Valid Until')] },
			// catField('manref_type', 'Type'), 
			cat_emcr_item_valuation:               { tabKey: 'accounting', tabLabel: 'Accounting', mandatory: false, fields: [catField('valuation_area', 'Valuation Area')] },
			cat_emcr_item_attachment:              { tabKey: 'attachment', tabLabel: 'Attachment Document & Keyword', mandatory: false, special: 'attachment',fields: [catField('file_code', 'Attachment Link')] },
			cat_emcr_item_costing_views:           { tabKey: 'costing', tabLabel: 'Costing', mandatory: false, fields: [catField('plant', 'Plant')] },
			cat_emcr_item_purchasings:             { tabKey: 'purchasing', tabLabel: 'Purchasing', mandatory: false, fields: [catField('plant', 'Plant')] },
			cat_emcr_item_sales:                   { tabKey: 'qm_sales', tabLabel: 'Sales Gen. Plant & Sales', mandatory: false, fields: [] },
			cat_emcr_item_warehouses:              { tabKey: 'warehouse', tabLabel: 'Warehouse Management', mandatory: false, fields: [] },
			cat_emcr_item_storage_types:           { tabKey: 'warehouse', tabLabel: 'Warehouse Management', mandatory: false, fields: [catField('plant', 'Plant')] },
			cat_emcr_item_inspection:              { tabKey: 'qm_work_sched', tabLabel: 'Quality Management & Work Scheduling',mandatory: false, fields: [] },
			cat_emcr_item_inspection_text:         { tabKey: 'data_inspection_text', tabLabel: 'INALUM Data & Inspection Text', mandatory: false, fields: [] },
			cat_emcr_item_alternative_uom:         { tabKey: 'alt_uom', tabLabel: 'Alternative UOM', mandatory: false, fields: [catField('alternative_unit_of_measure', 'Alternative UoM'), catField('numerator', 'Numerator'), catField('denumerator', 'Denominator')] },
		},
		conditional: null,
	};

	const add = (sectionKey, fields) => rules.sections[sectionKey].fields.push(...fields);

	if (code === 'ANTAM') {
		add('cat_emcr_item_plant', [
			catField('mrp_group', 'MRP Group'),
			catField('mrp_type', 'MRP Type'),
			catField('lot_size', 'Lot Size'),
			catField('mrp_controller', 'MRP Controller'),
			catField('reorder_point', 'Reorder Point'),
			catField('planned_delivery', 'Planned Delivery'),
			catField('safety_stock', 'Safety Stock'),
			catField('avail_check', 'Availability Check'),
		]);

		add('cat_emcr_item_valuation', [
			catField('valuation_class', 'Valuation Class'),
			catField('price_control', 'Price Control'),
		]);
	}

	if (code === 'TIMAH') {
		add('cat_emcr_item_location', [
			catField('bin', 'Storage Bin'),
		]);
		add('cat_emcr_item_sale_general_plants', [
			catField('profit_center', 'Profit Center'),
		]);
		add('cat_emcr_item_plant', [
			catField('mrp_controller', 'MRP Controller'),
			catField('lot_size', 'Lot Size'),
		]);
		add('cat_emcr_item_purchasings', [
			catField('pur_group', 'Purchasing Group'),
			catField('critical_part', 'Critical Part'),
		]);
		add('cat_emcr_item_valuation', [
			catField('valuation_class', 'Valuation Class'),
			catField('price_control', 'Price Control'),
			catField('price_unit', 'Price Unit'),
		]);
	}

	if (code === 'INALUM') {
		rules.basic.push(
			catField('cat_industry_sector', 'Industry Sector'),
			catField('cat_currency', 'Currency'),
			catField('cat_price_unit', 'Price Unit'),
			catField('cat_authorization_group', 'Authorization Group'),
			catField('cat_expiration_date', 'Expiration Date'),
			catField('cat_purchasing_val_key', 'Purchasing Value Key'),
		);
		add('cat_emcr_item_plants', [
			catField('period_in', 'Period Indicator'),
		]);
		add('cat_emcr_item_plant', [
			catField('sparepart_category', 'Item Category'),
			catField('sm_key', 'Strategy Group (SM Key)'),
		]);
		add('cat_emcr_item_sales', [
			catField('acc_ass_group', 'Account Assignment Group'),
			catField('sales_organization', 'Sales Organization'),
			catField('distr_channel', 'Distribution Channel'),
			catField('cash_discount', 'Cash Discount'),
			catField('item_cat_group', 'Item Category Code'),
			catField('material_statistic_code', 'Material Statistic Code'),
			catField('division', 'Division'),
			catField('departure_country', 'Departure Country'),
			catField('tax_category', 'Tax Category'),
			catField('tax_classification', 'Tax Classification'),
		]);
		add('cat_emcr_item_warehouses', [
			catField('warehouse_number', 'Warehouse Number'),
			catField('stock_replacement', 'Stock Replacement'),
			catField('stock_removal', 'Stock Removal'),
			catField('storage_section_ind', 'Storage Section Indicator'),
		]);
		add('cat_emcr_item_storage_types', [
			catField('warehouse_number', 'Warehouse Number'),
			catField('storage_type', 'Storage Type'),
		]);
		add('cat_emcr_item_inspection_text', [
			catField('language', 'Language'),
		]);
		rules.conditional = 'inalum';
	}

	return rules;
}

// ─── buildServiceRulesForItem (MCR008/MCR009) ────────────────────────────────

/**
 * Rules untuk MCR008 (create new service) / MCR009 (update service). Section berbasis
 * plant/material_type/material_group (Plant Data, MRP, Manufacture, dst.) tidak berlaku
 * untuk service, jadi cuma basic data + characteristic yang divalidasi — sama seperti
 * material, section characteristic pakai 'special: characteristic' (wajib per-baris
 * berdasarkan characteristic_item_name.is_mandatory, ditangani validateMcrItemCore()).
 * @returns {Object} rules
 */
export function buildServiceRulesForItem() {
	return {
		basic: [
			catField('cat_inc', 'Item Name'),
			catField('cat_item_type', 'Item Type'),
			catField('cat_service_category', 'Service Category'),
			catField('cat_service_group', 'Service Group'),
			catField('cat_uom', 'UoM'),
			catField('cat_valuation_class_service', 'Valuation Class'),
		],
		sections: {
			cat_emcr_item_characteristic_value: { tabKey: 'basic_data', tabLabel: 'Basic Data & Characteristic', mandatory: true, special: 'characteristic', fields: [] },
		},
		conditional: null,
	};
}

// ─── buildDynamicRulesForItem ────────────────────────────────────────────────

const INALUM_WAREHOUSE_MAT_TYPES = ['ZFGD', 'ZSFG', 'ZNOV'];

/**
 * Builds per-item validation rules by starting from the static company rules
 * then layering item-level conditions on top (mat_type, standard_description, etc.).
 * @param {Object} item        - one MCR item object
 * @param {string} companyCode - company code
 * @param {string} mcrType     - MCR type code (e.g. 'MCR001', 'MCR002')
 * @returns {Object|null}
 */
export function buildDynamicRulesForItem(item, companyCode, mcrType = 'MCR001') {
	// MCR008/MCR009 (Service Request) — bukan material, jadi bukan pakai
	// buildCatalogRulesForItem() (yang semuanya berbasis plant/material_type/material_group).
	const mcrTypeCodeEarly = (mcrType || '').toUpperCase();
	if (['MCR008', 'MCR009'].includes(mcrTypeCodeEarly)) return buildServiceRulesForItem();

	const rules = buildCatalogRulesForItem(companyCode);
	if (!rules) return null;

	const code        = (companyCode || '').toUpperCase();
	const mcrTypeCode = (mcrType || '').toUpperCase();
	const matType     = item?.cat_material_type ?? null;
	const addF        = (sectionKey, fields) => rules.sections[sectionKey].fields.push(...fields);

	if (code === 'ANTAM') {
		// Basic: hapus cat_gen_item_cat_grp, tambah cat_industry_sector
		rules.basic = rules.basic.filter(f => f.key !== 'cat_gen_item_cat_grp');
		rules.basic.push(
			catField('cat_industry_sector', 'Industry Sector'),
		);

		// Accounting: hanya valuation_class dan price_control yang wajib
		rules.sections['cat_emcr_item_valuation'].fields = [
			catField('valuation_class', 'Valuation Class'),
			catField('price_control',   'Price Control'),
		];

		// Purchasing: tidak ada yang wajib
		rules.sections['cat_emcr_item_purchasings'].fields = [];

		// Sales & Sales Gen Plant: tidak wajib, tidak ada field
		rules.sections['cat_emcr_item_sale_general_plants'].mandatory = false;
		rules.sections['cat_emcr_item_sale_general_plants'].fields    = [];
		rules.sections['cat_emcr_item_sales'].fields                  = [];

		// QM: tidak ada field yang divalidasi
		rules.sections['cat_emcr_item_inspection'].fields             = [];

		if (mcrTypeCode === 'MCR003') {
			// MCR003 (extend): material sudah tercatalog di SAP, item ini hanya menambah
			// assignment plant baru. Satu-satunya yang wajib: MRP.plant dan
			// Accounting.valuation_class/price_control — section lain (Plant Data,
			// Location, Manufacture, Costing, Storage Type, Alt UoM, dst.) tidak wajib
			// ada baris maupun field.
			rules.sections['cat_emcr_item_plants'].mandatory       = false;
			rules.sections['cat_emcr_item_plants'].fields          = [];
			rules.sections['cat_emcr_item_plant'].fields           = [catField('plant', 'Plant')];
			rules.sections['cat_emcr_item_manufacturer'].fields    = [
				catField('manufacture', 'Manufacture'),
				catField('source_type', 'Source Type'),
				catField('manufacture_ref', 'Manufacture Ref'),
				// catField('manref_type', 'Type'),
				catField('valid_until', 'Valid Until'),
			];
			rules.sections['cat_emcr_item_location'].mandatory     = false;
			rules.sections['cat_emcr_item_location'].fields        = [];
			rules.sections['cat_emcr_item_costing_views'].fields   = [];
			rules.sections['cat_emcr_item_storage_types'].fields   = [];
			rules.sections['cat_emcr_item_alternative_uom'].fields = [];
		} else if (mcrTypeCode === 'MCR002') {
			// MCR002: MRP hanya planned_delivery & mrp_controller yang wajib
			rules.sections['cat_emcr_item_plant'].fields = [
				catField('planned_delivery', 'Planned Delivery'),
				catField('mrp_controller',   'MRP Controller'),
			];
			// Manufacture, Location, Costing: tidak wajib ada baris maupun field
			rules.sections['cat_emcr_item_manufacturer'].fields      = [];
			rules.sections['cat_emcr_item_location'].mandatory       = false;
			rules.sections['cat_emcr_item_location'].fields          = [];
			rules.sections['cat_emcr_item_costing_views'].fields     = [];
		} else {
			// MCR001 (default): MRP fields lengkap
			// service_level sengaja TIDAK di sini — wajibnya kondisional, hanya kalau
			// mrp_controller MRS/SPP (lihat applyAntamConditionalCore).
			rules.sections['cat_emcr_item_plant'].fields = [
				catField('mrp_type',         'MRP Type'),
				catField('mrp_group',        'MRP Group'),
				catField('avail_check',      'Availability Check'),
				catField('safety_stock',     'Safety Stock'),
				catField('mrp_controller',   'MRP Controller'),
				catField('lot_size',         'Lot Size'),
				catField('planned_delivery', 'Planned Delivery'),
			];
			// Manufacture: hanya manufacture yang wajib
			rules.sections['cat_emcr_item_manufacturer'].fields = [
				catField('manufacture', 'Manufacture'),
			];
			// Location: hanya plant yang wajib
			rules.sections['cat_emcr_item_location'].fields = [catField('plant', 'Plant')];
			rules.conditional = 'antam';
		}
	}

	if (code === 'TIMAH') {
		// MRP: hanya mrp_type wajib; avail_check, mrp_controller, lot_size tidak wajib
		rules.sections['cat_emcr_item_plant'].fields = [catField('mrp_type', 'MRP Type')];

		// Accounting: tidak ada field yang wajib
		rules.sections['cat_emcr_item_valuation'].fields = [];

		// Purchasing: tidak ada field yang wajib
		rules.sections['cat_emcr_item_purchasings'].fields = [];

		// Location: hanya plant yang wajib; location dan bin tidak wajib
		rules.sections['cat_emcr_item_location'].fields = [catField('plant', 'Plant')];

		// Sales Gen Plant: tidak wajib ada minimal 1 row, profit_center tidak wajib
		rules.sections['cat_emcr_item_sale_general_plants'].mandatory = false;
		rules.sections['cat_emcr_item_sale_general_plants'].fields    = [];
	}

	if (code === 'INALUM') {
		// Sales sections are not mandatory by default — only re-enabled for specific mat types.
		rules.sections['cat_emcr_item_sale_general_plants'].mandatory = false;
		rules.sections['cat_emcr_item_sale_general_plants'].fields    = [];
		rules.sections['cat_emcr_item_sales'].fields                  = [];

		if (matType === 'ZFGD') {
			rules.sections['cat_emcr_item_sale_general_plants'].mandatory = true;
			rules.sections['cat_emcr_item_sale_general_plants'].fields    = [catField('plant', 'Plant')];
			addF('cat_emcr_item_sales', [
				catField('acc_ass_group',           'Account Assignment Group'),
				catField('sales_organization',       'Sales Organization'),
				catField('distr_channel',            'Distribution Channel'),
				catField('cash_discount',            'Cash Discount'),
				catField('item_cat_group',           'Item Category Code'),
				catField('material_statistic_code',  'Material Statistic Code'),
				catField('division',                 'Division'),
				catField('departure_country',        'Departure Country'),
				catField('tax_category',             'Tax Category'),
				catField('tax_classification',       'Tax Classification'),
			]);
		}

		if (INALUM_WAREHOUSE_MAT_TYPES.includes(matType)) {
			rules.basic.push(catField('cat_product_hierarchy', 'Product Hierarchy'));
			if (['ZFGD', 'ZSFG'].includes(matType)) {
				addF('cat_emcr_item_plant', [
					catField('requirement_group',     'Requirement Group'),
					catField('individual_collective', 'Individual / Collective'),
				]);	
			}
		}

		// if (matType === 'ZSPR' && item?.standard_description === 'MULTISOURCE') {
		if (item?.standard_description === 'MULTISOURCE') {
			rules.basic.push(catField('cat_standard_description', 'Standard Description'));
		}
	}
	// MCR003: material sudah masuk SAP sebelumnya, jadi field identitas material
	// (Item Name, Item Type, UoM, dst.) tidak divalidasi wajib — begitu juga section
	// Basic Data & Characteristic (tidak wajib minimal 1 baris characteristic value).
	// Section lain (MRP, Purchasing, Costing, dll) tetap divalidasi seperti biasa.
	if (mcrTypeCode === 'MCR003') {
		rules.basic = [];
		rules.sections['cat_emcr_item_characteristic_value'].mandatory = false;
	}
	return rules;
}

// ─── applyInalumConditionalCore ──────────────────────────────────────────────
/**
 * DIPANGGI SECARA INTERNAL DI/OLEH validateMcrItemCore()
 * Fungsinya: Validasi tambahan khusus INALUM yang tidak bisa diekspresikan lewat rule statis biasa (field wajib per section):
 */
export function applyInalumConditionalCore(item, errors, markTab) {
	const materialType   = item.cat_material_type ?? null;
	const accountings    = item.cat_emcr_item_valuation || [];
	const plants         = (item.cat_emcr_item_plants || []).map((p) => p.plant);
	const accountingAreas = accountings.map((a) => a.valuation_area);

	if (!Array.isArray(errors.cat_emcr_item_valuation)) errors.cat_emcr_item_valuation = [];

	if (!INALUM_EXEMPT_VALUATION_CLASS.includes(materialType)) {
		accountings.forEach((acc, j) => {
			if (isCatFieldEmpty(acc.valuation_class)) {
				errors.cat_emcr_item_valuation[j] = errors.cat_emcr_item_valuation[j] || {};
				errors.cat_emcr_item_valuation[j].valuation_class = 'Valuation Class is required';
				markTab('accounting', 'Accounting');
			}
		});
	}

	if (!INALUM_EXEMPT_PRICE_CONTROL.includes(materialType)) {
		accountings.forEach((acc, j) => {
			if (isCatFieldEmpty(acc.price_control)) {
				errors.cat_emcr_item_valuation[j] = errors.cat_emcr_item_valuation[j] || {};
				errors.cat_emcr_item_valuation[j].price_control = 'Price Control is required';
				markTab('accounting', 'Accounting');
			}
		});
	}

	const commonPlants = plants.filter((p) => accountingAreas.includes(p));
	if (commonPlants.length > 0 && (item.cat_emcr_item_costing_views || []).length === 0) {
		errors.cat_emcr_item_costing_views = 'Costing Views is required when plant and accounting area overlap';
		markTab('costing', 'Costing');
	}

	if (materialType === 'ZFGD') {
		if ((item.cat_emcr_item_sales || []).length === 0) {
			errors.cat_emcr_item_sales = 'Sales is required for ZFGD material type';
			markTab('qm_sales', 'Sales, Gen. Plant & Sales');
		}
		if ((item.cat_emcr_item_warehouses || []).length === 0) {
			errors.cat_emcr_item_warehouses = 'Warehouses is required for ZFGD material type';
			markTab('warehouse', 'Warehouse Management');
		}
		if ((item.cat_emcr_item_storage_types || []).length === 0) {
			errors.cat_emcr_item_storage_types = 'Storage Types is required for ZFGD material type';
			markTab('warehouse', 'Warehouse Management');
		}
	}
}

// ─── applyAntamDraftConditionalCore ──────────────────────────────────────────
// const ANTAM_DRAFT_VALUATION_MAT_TYPES = ['ZM02', 'ZM03', 'ZM04', 'ZM05', 'ZM06'];
const ANTAM_DRAFT_VALUATION_MAT_TYPES = [];
/**
 * DIPANGGI SECARA INTERNAL DI/OLEH validateMcrItemCore() (:638) SAAT (( rules.conditional === 'antamDraft' )) YANG DISET OLEH buildAntamDraftRulesForItem()
 * FUNGSI INI DIGUNAKAN OLEH validateAntamDraftForm()
 * lewat rules.sections statis:
 * - std_price wajib hanya kalau price_control === 'S'
 * - valuation_type wajib hanya untuk material type tertentu
 * cat_locations[i].location, cat_warehouses[i].warehouse_number, dan
 * cat_storage_types[i].warehouse_number/storage_type sengaja TIDAK divalidasi wajib —
 * boleh kosong / array-nya kosong sama sekali untuk ANTAM draft.
 */
export function applyAntamDraftConditionalCore(item, errors, markTab) {
	const valuations = item.cat_emcr_item_valuation || [];
	if (!Array.isArray(errors.cat_emcr_item_valuation)) errors.cat_emcr_item_valuation = [];

	valuations.forEach((val, idx) => {
		if (val.price_control === 'S' && isCatFieldEmpty(val.std_price)) {
			errors.cat_emcr_item_valuation[idx] = errors.cat_emcr_item_valuation[idx] || {};
			errors.cat_emcr_item_valuation[idx].std_price = 'Std Price is required when Price Control is S';
			markTab('accounting', 'Accounting');
		}
		if (ANTAM_DRAFT_VALUATION_MAT_TYPES.includes(item.cat_material_type) && isCatFieldEmpty(val.valuation_type)) {
			errors.cat_emcr_item_valuation[idx] = errors.cat_emcr_item_valuation[idx] || {};
			errors.cat_emcr_item_valuation[idx].valuation_type = `Valuation Type is required for material type ${item.cat_material_type}`;
			markTab('accounting', 'Accounting');
		}
	});
}

// ─── applyAntamConditionalCore ───────────────────────────────────────────────
const ANTAM_SERVICE_LEVEL_MRP_CONTROLLERS = ['MRS', 'SPP'];
/**
 * DIPANGGIL INTERNAL OLEH validateMcrItemCore() (:640) SAAT (( rules.conditional === 'antam' )) DAN MENJALANKAN RULES YANG DISET buildDynamicRulesForItem()
 * Conditional rule dari buildDynamicRulesForItem('ANTAM', 'MCR001')
 * UNTUK KHUSUS ANTAM + MCR001 (bukan MCR002/003)
 * FUNGSI INI DIGUNAKAN OLEH validateAllTabsCatalogForm() / validateSingleMcrItem() UNTUK ANTAM
 * - service_level di cat_emcr_item_plant wajib hanya kalau mrp_controller MRS atau SPP.
 */
export function applyAntamConditionalCore(item, errors, markTab) {
	const plants = item.cat_emcr_item_plant || [];
	if (!Array.isArray(errors.cat_emcr_item_plant)) errors.cat_emcr_item_plant = [];

	plants.forEach((row, idx) => {
		if (ANTAM_SERVICE_LEVEL_MRP_CONTROLLERS.includes(row.mrp_controller) && isCatFieldEmpty(row.service_level)) {
			errors.cat_emcr_item_plant[idx] = errors.cat_emcr_item_plant[idx] || {};
			errors.cat_emcr_item_plant[idx].service_level = 'Service Level is required when MRP Controller is MRS or SPP';
			markTab('mrp', 'MRP');
		}
	});
}

// ─── validateMcrItemCore BENER BENER UTAMA ─────────────────────────────────────────────────────
/**
 * BENER BENER UTAMA DIPANGGIL DARI McrFooter.vue DI 3 TEMPAT ( validateAntamDraftForm(), validateAllTabsCatalogForm(), dan validateSingleMcrItem() )
 * Memvalidasi satu MCR item terhadap rules (apapun yang dioper (baik dari buildCatalogRulesForItem, buildDynamicRulesForItem, atau buildAntamDraftRulesForItem)).
 * @param {Object} item   - satu object MCR item
 * @param {Object} rules  - hasil buildCatalogRulesForItem / buildCatalogRules
 * @returns {{ errors: Object, tabErrors: Object }}
 */
export function validateMcrItemCore(item, rules) {
	const errors    = {};
	const tabErrors = {};
	const markTab   = (tabKey, tabLabel) => { tabErrors[tabKey] = tabLabel; };

	// 1. Field level basic
	rules.basic.forEach((r) => {
		if (isCatFieldEmpty(item[r.key])) {
			errors[r.key] = `${r.message} is required`;
			markTab('basic_data', 'Basic Data & Characteristic');
		}
	});

	// 2. Section arrays
	Object.entries(rules.sections).forEach(([arrayKey, sec]) => {
		const list = item[arrayKey] || [];
		errors[arrayKey] = [];

		if (sec.mandatory && list.length === 0) {
			errors[`${arrayKey}`] = `${sec.tabLabel} value is required`;
			markTab(sec.tabKey, sec.tabLabel);
			return;
		}

		list.forEach((row, idx) => {
			const obj = {};

			if (sec.special === 'characteristic') {
				if (isCatFieldEmpty(row.cinv_code) && row?.characteristic_item_name?.is_mandatory === 'YES') {
					obj.cinv_code = 'Character Value is required';
					markTab(sec.tabKey, sec.tabLabel);
				}
				if (Object.keys(obj).length) errors[arrayKey][idx] = obj;
				return;
			}

			if (sec.special === 'attachment' && !row.id) return;
			sec.fields.forEach((r) => {
				if (isCatFieldEmpty(row[r.key])) {
					obj[r.key] = `${r.message} is required`;
					markTab(sec.tabKey, sec.tabLabel);
				}
			});
			if (Object.keys(obj).length) errors[arrayKey][idx] = obj;
		});
	});

	// 3. Conditional (INALUM basic_data.js rules, Antam draft-per-item, atau Antam service_level)
	if (rules.conditional === 'inalum') {
		applyInalumConditionalCore(item, errors, markTab);
	} else if (rules.conditional === 'antamDraft') {
		applyAntamDraftConditionalCore(item, errors, markTab);
	} else if (rules.conditional === 'antam') {
		applyAntamConditionalCore(item, errors, markTab);
	}
	return { errors, tabErrors };
}

// ─── normalizeItemPayload ────────────────────────────────────────────────────

const sanitizeAlnumUpper = (val) => (val ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
const normalize = (val) => {
	if (!val) return val;
	if (typeof val === 'object' && val !== null) {
		if (val.code) return val.code;
		if (val.warehouse_number) return val.warehouse_number;
		if (val.label && typeof val.label === 'string' && val.label.includes(' - ')) return val.label.split(' - ')[0];
		if (val.label) return val.label;
	}
	return val;
};

/**
 * Normalisasi satu MCR item menjadi payload siap kirim ke API.
 * Mirror dari pola .map(y => {...}) di dalam preparePayload('cataloguer') McrFooter.vue.
 * @param {Object} y          - satu object MCR item
 * @param {string} clientCode - this.clientCode dari McrFooter
 * @param {string} mcrCode    - this.mcrHeader.mcr_code dari McrFooter
 * @returns {Object} payload ternormalisasi
 */
export function normalizeItemPayload(y, clientCode, mcrCode, mapHardcodeMcr = {}, companyCode = '', mcrType = '') {
	// ANTAM: baris location/warehouse/storage_type yang field esensialnya kosong (belum
	// diisi user) sengaja dibuang dari payload — sama seperti buildAntamFormData()
	// (McrFooter.vue) — karena BE menolak object dengan field required kosong, tapi array
	// kosong [] aman. Company lain (TIMAH/INALUM) tidak terpengaruh.
	const isAntamCompany = (companyCode || '').toUpperCase() === 'ANTAM';

	const payloadMnf = y.cat_emcr_item_manufacturer.map((mf) => ({
		mpn_number:                  mf.mpn_number ?? null,
		manufacture:                 mf.catalog_manufacture?.code || mf.manufacture,
		manufacture_ref:             mf.manufacture_ref,
		manufacture_ref_description: mf.manufacture_ref_description,
		source_type:                 normalize(mf.source_type),
		manref_type:                 mf.manref_type ? normalize(mf.manref_type) : null,
		position:                    mf.position ? String(mf.position) : null,
		spn:                         sanitizeAlnumUpper(mf.manufacture_ref),
		valid_until:                 mf.valid_until,
		valid_start:                 mf.valid_start,
		notes:                       mf.notes,
	}));

	const payloadEqp = y.cat_emcr_item_equipment.map((eq) => ({
		client:        clientCode,
		plant:         normalize(eq.plant),
		equipment:     eq.equipment_detail?.code || eq.equipment,
		qty_installed: String(eq.qty_installed ?? ''),
	}));

	const payloadPlant = (y.cat_emcr_item_plants || []).map((pl) => ({
		client:        clientCode,
		plant:         normalize(pl.plant),
		period_in:     pl.period_in || null,
		unit_of_issue: pl.unit_of_issue || null,
	}));

	const payloadKeword = y.cat_emcr_item_keyword.map((kw) => ({ keyword: kw.keyword }));

	const payloadLoc = (y.cat_emcr_item_location || [])
		.filter((lc) => !isAntamCompany || !isCatFieldEmpty(lc.location))
		.map((lc) => ({
			client:   clientCode,
			plant:    normalize(lc.plant),
			location: lc.location_detail?.code || lc.location,
			bin:      lc.bin,
			stock:    lc.stock != null ? String(lc.stock) : null,
		}));

	const payloadVal = y.cat_emcr_item_valuation.map((vl) => ({
		client:              clientCode,
		valuation_area:      normalize(vl.valuation_area),
		valuation_category:  normalize(vl.valuation_category),
		valuation_type:      normalize(vl.valuation_type),
		price_control:       normalize(vl.price_control),
		moving_price:        vl.moving_price != null ? String(vl.moving_price) : null,
		std_price:           vl.std_price != null ? String(vl.std_price) : null,
		price_unit:          vl.price_unit != null ? String(vl.price_unit) : null,
		price_reference:     vl.price_reference != null ? String(vl.price_reference) : null,
		valuation_class:     normalize(vl.valuation_class),
		ml_active:           vl.ml_active,
		ml_settle:           vl.ml_settle ? String(vl.ml_settle) : null,
		qty_struct:          vl.qty_struct,
		overhead_group:      vl.overhead_group ?? '',
		price_determination: vl.price_determination,
	}));

	const payloadCharVal = y.cat_emcr_item_characteristic_value.map((cv) => ({
		cin_code:            cv.cin_code,
		cinv_code:           cv.cinv_code ?? null,
		view:                cv.view ?? null,
		is_multiple:         cv.is_multiple ?? null,
		classification_type: '',
	}));

	const payloadAttach = y.cat_emcr_item_attachment
		.filter((att) => att.file_code)
		.map((att) => ({
			file_code: att.file_code,
			sap: att.sap,
			status_sap: att.status_sap,
			status_deletion: att.status_deletion,
			document_number: att.document_number,
			application_id: att.application_id,
			file_id: att.file_id,
			del_ind: att.del_ind,
			folder_name: att.folder_name,
		}));

	const payloadSales = (y.cat_emcr_item_sales || []).map((sl) => ({
		acc_ass_group:           normalize(sl.acc_ass_group),
		sales_organization:      normalize(sl.sales_organization),
		distr_channel:           normalize(sl.distr_channel),
		cash_discount:           sl.cash_discount,
		item_cat_code:           sl.item_cat_group,
		material_statistic_code: sl.material_statistic_code,
		division:                sl.division,
		material_price:          sl.material_price,
		client:                  clientCode,
		delivering_plant:        sl.delivering_plant,
		sales_unit:              sl.sales_unit,
		commission_group:        sl.commission_group,
		departure_country:       sl.departure_country,
		tax_category:            sl.tax_category,
		tax_classification:      sl.tax_classification,
		gen_item_cat_grp:        sl.gen_item_cat_grp,
	}));

	const payloadWarehouses = (y.cat_emcr_item_warehouses || [])
		.filter((wh) => !isAntamCompany || !isCatFieldEmpty(wh.warehouse_number))
		.map((wh) => ({
			warehouse_number:    wh.warehouse_number,
			client:              clientCode,
			plant:               wh.plant,
			stock_removal:       normalize(wh.stock_removal),
			stock_replacement:   normalize(wh.stock_replacement),
			storage_section_ind: normalize(wh.storage_section_ind),
		}));

	const payloadStorageTypes = (y.cat_emcr_item_storage_types || [])
		.filter((st) => !isAntamCompany || !isCatFieldEmpty(st.storage_type))
		.map((st) => ({
			client:           clientCode,
			plant:            normalize(st.plant),
			warehouse_number: normalize(st.warehouse_number),
			storage_type:     normalize(st.storage_type),
			storage_bin:      normalize(st.storage_bin),
		}));

	const payloadSalesGenPlants = (y.cat_emcr_item_sale_general_plants || []).map((sgp) => ({
		client:           clientCode,
		plant:            sgp.plant,
		loading_group:    sgp.loading_group,
		profit_center:    sgp.profit_center,
	}));

	const payloadMrps = (y.cat_emcr_item_plant || []).map((pl) => ({
		client:                       clientCode,
		plant:                        normalize(pl.plant),
		sparepart_category:           pl.sparepart_category,
		mrp_profile:	              pl.mrp_profile,
		mrp_type:                     normalize(pl.mrp_type),
		mrp_controller:               normalize(pl.mrp_controller),
		mrp_group:                    pl.mrp_group,
		lot_size:                     pl.lot_size,
		reorder_point:                pl.reorder_point != null ? String(pl.reorder_point) : null,
		max_stock:                    pl.max_stock != null ? String(pl.max_stock) : null,
		abc_indicator:                normalize(pl.abc_indicator),
		min_lotsize:                  pl.min_lotsize != null ? String(pl.min_lotsize) : null,
		max_lotsize:                  pl.max_lotsize != null ? String(pl.max_lotsize) : null,
		rounding_value:               pl.rounding_value != null ? String(pl.rounding_value) : null,
		fixed_lot:                    pl.fixed_lot != null ? String(pl.fixed_lot) : null,
		safety_stock:                 pl.safety_stock != null ? String(pl.safety_stock) : null,
		sloc_for_ep:                  normalize(pl.sloc_for_ep),
		procurement_type:             normalize(pl.procurement_type),
		co_product:                   pl.co_product,
		avail_check:                  normalize(pl.avail_check),
		requirement_group:            normalize(pl.requirement_group),
		individual_collective:        pl.individual_collective,
		special_procurement_type:     normalize(pl.special_procurement_type),
		planned_delivery:             pl.planned_delivery != null ? String(pl.planned_delivery) : null,
		in_house_production:          pl.in_house_production != null ? String(pl.in_house_production) : null,
		gr_processing_time:           pl.gr_processing_time != null ? String(pl.gr_processing_time) : null,
		sm_key:                       normalize(pl.sm_key),
		backflush:                    pl.backflush,
		strategy_group:               pl.strategy_group,
		consumption_mode:             pl.consumption_mode,
		consumption_periode_forward:  pl.consumption_periode_forward != null ? String(pl.consumption_periode_forward) : null,
		consumption_periode_backward: pl.consumption_periode_backward != null ? String(pl.consumption_periode_backward) : null,
		mix_mrp:                      pl.mix_mrp,
		selection_methode:            pl.selection_methode,
		service_level:                pl.service_level != null ? String(pl.service_level) : null,
		material_freight_group:       pl.material_freight_group != null ? String(pl.material_freight_group) : null,
	}));

	const payloadWorkSchedViews = [];
	// const payloadWorkSchedViews = (y.cat_emcr_item_work_schedulling_views || []).map((wsv) => ({
	// 	client:                         clientCode,
	// 	plant:                          normalize(wsv.plant),
	// 	production_supervisor:          wsv.production_supervisor,
	// 	production_scheduling_profile:  wsv.production_scheduling_profile,
	// 	production_storage_location:    wsv.production_storage_location,
	// 	underdelivery_toler_limit:      wsv.underdelivery_toler_limit,
	// 	overdelivery_toler_limit:       wsv.overdelivery_toler_limit,
	// }));

	const payloadPurchasing = (y.cat_emcr_item_purchasings || []).map((pcs) => ({
		client:        clientCode,
		plant:         normalize(pcs.plant),
		pur_group:     normalize(pcs.pur_group) || '',
		order_unit:    pcs.order_unit,
		critical_part: pcs.critical_part,
		automation_po: pcs.automation_po,
		plant_sp:      pcs.plant_sp,
	}));

	const payloadCostView = (y.cat_emcr_item_costing_views || []).map((cvs) => ({
		client:            clientCode,
		plant:             normalize(cvs.plant),
		no_costing:        cvs.no_costing,
		variance_key:      cvs.variance_key,
		qty_struct:        cvs.qty_struct,
		original_material: cvs.original_material,
		overhead_group:    cvs.overhead_group,
		costing_lot_size:  cvs.costing_lot_size != null ? String(cvs.costing_lot_size) : null,
	}));

	const payloadQualManag = (y.cat_emcr_item_inspection || []).map((ins) => ({
		client:                   clientCode,
		plant:                    normalize(ins.plant),
		inspect_with_task_list:   ins.inspect_with_task_list,
		inspection_type:          ins.inspection_type,
		post_to_qi_stock:         ins.post_to_qi_stock,
		automatic_assignment:     ins.automatic_assignment,
		inspect_charcs:           ins.inspect_charcs,
		inspect_percentage:       ins.inspect_percentage != null ? String(ins.inspect_percentage) : null,
		skips_allowed:            ins.skips_allowed,
		q_score_procedure:        ins.q_score_procedure,
		inspect_type_matl_active: ins.inspect_type_matl_active,
		preferred_insp_type:      ins.preferred_insp_type,
	}));

	const payloadInsText = (y.cat_emcr_item_inspection_text || []).map((it) => ({
		language:        it.language,
		inspection_text: it.inspection_text,
	}));

	const payloadDataOne = (y.cat_emcr_inalum_datas || []).map((ido) => ({
		client:                  clientCode,
		plant:                   normalize(ido.plant),
		category_item:           ido.category_item,
		unspsc_x_reference:      ido.unspsc_x_reference,
		mind_id_number:          ido.mind_id_number,
		review_date_inalum_data: ido.review_date_inalum_data,
		remark_inalum_data:      ido.remark_inalum_data,
	}));

	const payloadAltUom = (y.cat_emcr_item_alternative_uom || []).map((alt) => ({
		client:                  clientCode,
		alternative_unit_of_measure: alt.alternative_unit_of_measure,
		numerator:    alt.numerator,
		denumerator:  alt.denumerator,
		source_field: alt.source_field,
	}));

	const payload = {
		id:                           y.id,
		mcr_code:                     mcrCode,
		mcr_item_code:                y.mcr_item_code,
		client:                       clientCode,
		cat_inc:                      y.catalog_inc?.inc || (typeof y.cat_inc === 'string' && y.cat_inc.includes(' - ') ? y.cat_inc.split(' - ')[0] : y.cat_inc),
		cat_item_type:                normalize(y.cat_item_type),
		cat_uom:                      y.catalog_uom?.code || (typeof y.cat_uom === 'string' && y.cat_uom.includes(' - ') ? y.cat_uom.split(' - ')[0] : y.cat_uom),
		cat_material_type:            normalize(y.cat_material_type),
		cat_material_group:           normalize(y.cat_material_group),
		cat_old_material_number:      y.cat_old_material_number,
		cat_industry_sector:          y.cat_industry_sector,
		specification: 		          y.specification,
		cat_ext_material_group:       y.cat_ext_material_group,
		cat_notes:                    y.cat_notes,
		cat_short_description:        y.cat_short_description,
		cat_po_vertical:              y.cat_po_vertical,
		cat_po_horizontal:            y.cat_po_horizontal,
		cat_trans_grp:            	  normalize(y.cat_trans_grp),
		cat_batch_management:         y.cat_batch_management,
		cat_product_hierarchy:        y.cat_product_hierarchy,
		cat_standard_description:     y.cat_standard_description,
		cat_currency:                 y.cat_currency,
		cat_lab_office:               y.cat_lab_office,
		cat_price_unit:               y.cat_price_unit != null ? String(y.cat_price_unit) : null,
		cat_authorization_group:      y.cat_authorization_group,
		cat_net_weight:               y.cat_net_weight,
		cat_weight_unit:              y.cat_weight_unit,
		cat_expiration_date:          y.cat_expiration_date,
		cat_manufacture_part_profile: (Array.isArray(payloadMnf) && payloadMnf.length > 0) ? mapHardcodeMcr?.manufacture_part_profile || "" : y.cat_manufacture_part_profile || "",
		cat_purchasing_val_key:       y.cat_purchasing_val_key,
		cat_service_category:         y.cat_service_category,
		cat_service_group:            y.cat_service_group,
		cat_valuation_class_service:  y.cat_valuation_class_service,
		cat_emcr_manufacturers:          payloadMnf,
		cat_emcr_equipments:             payloadEqp,
		cat_emcr_plants:                 payloadPlant,
		cat_emcr_keywords:               payloadKeword,
		cat_emcr_locations:              payloadLoc,
		cat_emcr_accountings:            payloadVal,
		cat_emcr_characteristic_values:  payloadCharVal,
		cat_emcr_attachments:            payloadAttach,
		cat_emcr_sales:                  payloadSales,
		cat_emcr_warehouses:             payloadWarehouses,
		cat_emcr_storage_types:          payloadStorageTypes,
		cat_emcr_sales_general_plants:   payloadSalesGenPlants,
		cat_emcr_mrps:                   payloadMrps,
		cat_emcr_work_schedulling_views: payloadWorkSchedViews,
		cat_emcr_purchasings:            payloadPurchasing,
		cat_emcr_costing_views:          payloadCostView,
		cat_emcr_quality_managements:    payloadQualManag,
		cat_emcr_inspection_texts:       payloadInsText,
		cat_emcr_inalum_datas:           payloadDataOne,
		cat_emcr_alternative_uoms:       payloadAltUom,
	};

	if ((companyCode || '').toUpperCase() === 'ANTAM' && (mcrType || '').toUpperCase() === 'MCR003') {
		return buildAntamExtendPayload(payload);
	}

	return payload;
}


// ─── ANTAM MCR003 (extend) payload trimming ──────────────────────────────────
// Untuk ANTAM + MCR003, material yang di-extend sudah tercatalog di SAP — hanya
// field yang benar-benar diisi user pada flow ini yang boleh terlempar ke
// backend. Field lain (termasuk 'plant'/'valuation_area' yang ke-cascade
// otomatis lewat pola ensurePlantRow) dikosongkan supaya tidak ikut mengirim
// data lama/ikutan yang bukan input eksplisit user.
const ANTAM_MCR003_TOP_LEVEL_KEEP = ['id', 'mcr_code', 'mcr_item_code', 'client', 'material_type', 'cat_material_type', 'material_group', 'cat_material_group','cat_inc','cat_item_type','cat_uom'];
const ANTAM_MCR003_PLANT_KEEP       = ['client', 'plant'];
const ANTAM_MCR003_MRP_DROP = ['mrp_controller'];
const ANTAM_MCR003_LOCATION_KEEP  = ['client', 'plant', 'location'];
const ANTAM_MCR003_VALUATION_KEEP = ['client', 'valuation_area', 'valuation_type', 'valuation_class', 'price_control', 'std_price'];
const ANTAM_MCR003_MANUFACTURER_KEEP = ['manufacture', 'source_type', 'manufacture_ref', 'manufacture_ref_description', 'manref_type', 'valid_until', 'valid_start'];
const ANTAM_MCR003_ATTACHMENT_KEEP = ['file_code'];
const ANTAM_MCR003_KEYWORD_KEEP = ['keyword'];

const trimRowToKeys = (row, keepKeys) => {
	const trimmed = {};
	Object.keys(row).forEach((key) => {
		trimmed[key] = keepKeys.includes(key) ? row[key] : null;
	});
	return trimmed;
};

const trimRowExcludingKeys = (row, dropKeys) => {
	const trimmed = { ...row };
	dropKeys.forEach((key) => { trimmed[key] = null; });
	return trimmed;
};

const EMPTIED_ARRAYS_FOR_ANTAM_MCR003 = [
	'cat_emcr_equipments', 
	'cat_emcr_characteristic_values', 'cat_emcr_sales',
	'cat_emcr_warehouses', 'cat_emcr_storage_types', 'cat_emcr_sales_general_plants',
	'cat_emcr_work_schedulling_views', 'cat_emcr_purchasings', 'cat_emcr_costing_views',
	'cat_emcr_quality_managements', 'cat_emcr_inspection_texts', 'cat_emcr_inalum_datas',
	'cat_emcr_alternative_uoms',
];

function buildAntamExtendPayload(payload) {
	const trimmed = { ...payload };

	Object.keys(trimmed).forEach((key) => {
		if (ANTAM_MCR003_TOP_LEVEL_KEEP.includes(key)) return;
		if (Array.isArray(trimmed[key])) return;
		trimmed[key] = null;
	});

	trimmed.cat_emcr_plants        = (trimmed.cat_emcr_plants || []).map((row) => trimRowToKeys(row, ANTAM_MCR003_PLANT_KEEP));
	trimmed.cat_emcr_mrps          = (trimmed.cat_emcr_mrps || []).map((row) => trimRowExcludingKeys(row, ANTAM_MCR003_MRP_DROP));
	trimmed.cat_emcr_locations     = (trimmed.cat_emcr_locations || []).map((row) => trimRowToKeys(row, ANTAM_MCR003_LOCATION_KEEP));
	trimmed.cat_emcr_accountings   = (trimmed.cat_emcr_accountings || []).map((row) => trimRowToKeys(row, ANTAM_MCR003_VALUATION_KEEP));
	trimmed.cat_emcr_manufacturers = (trimmed.cat_emcr_manufacturers || []).map((row) => trimRowToKeys(row, ANTAM_MCR003_MANUFACTURER_KEEP));
	trimmed.cat_emcr_attachments   = (trimmed.cat_emcr_attachments || []).map((row) => trimRowToKeys(row, ANTAM_MCR003_ATTACHMENT_KEEP));
	trimmed.cat_emcr_keywords      = (trimmed.cat_emcr_keywords || []).map((row) => trimRowToKeys(row, ANTAM_MCR003_KEYWORD_KEEP));

	EMPTIED_ARRAYS_FOR_ANTAM_MCR003.forEach((key) => { trimmed[key] = []; });

	return trimmed;
}
